import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileText, Plus, Clock, CheckCircle, Send, Eye, XCircle } from 'lucide-react'
import { listEnvelopes, type EnvelopeWithRecipients } from '../services/envelopeService'
import toast from 'react-hot-toast'

const STATUS_CONFIG: Record<string, { label: string; icon: typeof Clock; color: string }> = {
  draft: { label: 'Borrador', icon: FileText, color: 'text-legal-500 bg-legal-100 dark:bg-legal-700' },
  sent: { label: 'Enviado', icon: Send, color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30' },
  completed: { label: 'Completado', icon: CheckCircle, color: 'text-green-600 bg-green-100 dark:bg-green-900/30' },
  voided: { label: 'Anulado', icon: XCircle, color: 'text-red-600 bg-red-100 dark:bg-red-900/30' },
}

export function DocumentsPage() {
  const navigate = useNavigate()
  const [envelopes, setEnvelopes] = useState<EnvelopeWithRecipients[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadEnvelopes()
  }, [])

  async function loadEnvelopes() {
    try {
      setLoading(true)
      const data = await listEnvelopes()
      setEnvelopes(data)
    } catch (err) {
      console.error('Error loading envelopes:', err)
      toast.error('Error al cargar documentos')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-legal-900 dark:text-white flex items-center gap-3">
            <FileText className="text-primary-600 dark:text-primary-400" size={28} />
            Mis Documentos
          </h1>
          <p className="text-sm text-legal-500 dark:text-legal-400 mt-1">
            Documentos enviados y recibidos para firma
          </p>
        </div>
        <button
          onClick={() => navigate('/documentos/nuevo')}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
        >
          <Plus size={18} />
          Nuevo Documento
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-legal-500 dark:text-legal-400">
          Cargando documentos...
        </div>
      ) : envelopes.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700">
          <FileText size={48} className="mx-auto text-legal-300 dark:text-legal-600 mb-4" />
          <p className="text-legal-600 dark:text-legal-300 mb-2">No tenes documentos todavia</p>
          <p className="text-sm text-legal-400 dark:text-legal-500 mb-4">
            Subi un documento, firmalo y envialo para que otra persona lo firme
          </p>
          <button
            onClick={() => navigate('/documentos/nuevo')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
          >
            Crear Documento
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {envelopes.map((env) => {
            const statusCfg = STATUS_CONFIG[env.status] || STATUS_CONFIG.draft
            const StatusIcon = statusCfg.icon
            const recipients = env.sgw_envelope_recipients || []
            const signedCount = recipients.filter(r => r.status === 'signed').length

            return (
              <button
                key={env.id}
                onClick={() => navigate(`/documentos/${env.id}`)}
                className="w-full text-left bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 p-4 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-legal-900 dark:text-white truncate">
                      {env.title}
                    </h3>
                    <p className="text-xs text-legal-500 dark:text-legal-400 mt-1">
                      {env.original_filename}
                    </p>
                    {recipients.length > 0 && (
                      <div className="flex items-center gap-3 mt-2">
                        {recipients.map((r) => (
                          <span
                            key={r.id}
                            className="flex items-center gap-1 text-xs text-legal-500 dark:text-legal-400"
                          >
                            {r.status === 'signed' ? (
                              <CheckCircle size={12} className="text-green-500" />
                            ) : r.status === 'viewed' ? (
                              <Eye size={12} className="text-blue-500" />
                            ) : (
                              <Clock size={12} className="text-legal-400" />
                            )}
                            {r.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 ml-4">
                    <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
                      <StatusIcon size={12} />
                      {statusCfg.label}
                    </span>
                    <span className="text-xs text-legal-400 dark:text-legal-500">
                      {new Date(env.created_at).toLocaleDateString('es-AR')}
                    </span>
                    {recipients.length > 0 && (
                      <span className="text-xs text-legal-400 dark:text-legal-500">
                        {signedCount}/{recipients.length} firmado{recipients.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
